import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Zap, UserPlus, AlertCircle } from "lucide-react";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("technicien");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Page reservee aux administrateurs (le backend refuse de toute facon les autres).
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caracteres");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, role);
      toast.success(`Utilisateur ${email} cree`);
      setName(""); setEmail(""); setPassword(""); setConfirmPassword(""); setRole("technicien");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4" data-testid="register-page">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/661442fe-2c60-4494-bb2b-cd4283431309/images/f9396613ee0661711c3dd0b39ac0ea77d38339390843d1ee752c5835db3a8a51.png)`,
        }}
      />
      <div className="absolute inset-0 bg-cyber-bg/90 backdrop-blur-sm" />
      <div className="absolute inset-0 cyber-grid-bg opacity-50" />

      <div className="relative w-full max-w-md">
        <div className="bg-cyber-surface/90 backdrop-blur-xl border border-neon-blue/20 rounded-xl p-8 neon-glow-blue">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 border border-neon-blue/30 rounded-xl flex items-center justify-center neon-glow-blue">
              <Zap className="w-6 h-6 text-neon-blue" />
            </div>
            <div>
              <h1 className="font-heading font-black text-2xl tracking-tight text-white leading-none neon-text-blue">PROJET LX HEV</h1>
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-neon-blue/60">Gestion batteries hybrides</span>
            </div>
          </div>

          <h2 className="font-heading font-bold text-lg text-white mb-1">Nouvel utilisateur</h2>
          <p className="text-sm text-muted-foreground mb-6">Creez un compte utilisateur (reserve aux administrateurs)</p>

          {error && (
            <div className="flex items-center gap-2 bg-neon-red-muted border border-neon-red/30 rounded-lg px-3 py-2.5 mb-4 neon-glow-red" data-testid="register-error">
              <AlertCircle className="w-4 h-4 text-neon-red shrink-0" />
              <span className="text-sm text-neon-red">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Nom complet</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jean Dupont" required className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg" data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" required className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg" data-testid="register-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Mot de passe</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 caracteres" required className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg" data-testid="register-password-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Confirmer</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirmer" required className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg" data-testid="register-confirm-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Role</Label>
              <Select value={role} onValueChange={setRole} data-testid="register-role-select">
                <SelectTrigger className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-sm h-10 rounded-lg" data-testid="register-role-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technicien" data-testid="role-technicien">Technicien</SelectItem>
                  <SelectItem value="admin" data-testid="role-admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide hover:from-neon-blue-hover hover:to-neon-purple h-10 rounded-lg neon-glow-blue" data-testid="register-submit-button">
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" />Creer le compte</>}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/dashboard" className="text-sm text-neon-blue hover:text-neon-cyan hover:underline font-medium transition-colors" data-testid="login-link">Retour a l'accueil</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
